import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { NewJob } from '~/common/entities/NewJob/NewJobTypes';
import { Job } from '~/common/entities/Project/ProjectTypes';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios from 'axios';

export enum NewJobKeys {
  NEWJOBS = 'NEWJOBS',
  NEWJOB = 'NEWJOB',
  NEWJOBRECORD = 'NEWJOBRECORD',
  NEWJOBRECORDCOUNT = 'NEWJOBRECORDCOUNT',
}

export interface NewJobRecord {
  id: number;
  new_job: NewJob;
  job: number;
  start: number;
  end: number;
  duration: number;
  amount: number;
  comment: string;
  employee: number;
}

export const NewJobQueries = {
  async getNewJobRecordCount() {
    const res = await axios.get<{ count: number }>(`/record/new_job/count/`);
    return unwrap(res);
  },
  async getAllNewJobRecords(filterState: {
    employee?: number;
    project?: number;
  }) {
    let queryParams = '';

    if (filterState && filterState.employee) {
      queryParams += `?employee=${filterState.employee}`;
    }

    if (filterState && filterState.project) {
      if (queryParams) {
        queryParams += `&project=${filterState.project}`;
      } else {
        queryParams += `?project=${filterState.project}`;
      }
    }
    const res = await axios.get<NewJobRecord[]>(
      `/record/new_job/${queryParams}`,
    );
    return unwrap(res);
  },
  async getAllNewJobsOfProject(project: IdProvider) {
    if (!project?.id) {
      return null;
    }
    const res = await axios.get<NewJob[]>(`/new_jobs/${project.id}/`);
    return unwrap(res);
  },
  async getNewJob(project: IdProvider) {
    if (!project?.id) {
      return null;
    }
    const res = await axios.get<NewJob>(`/new_job/${project.id}/`);
    return unwrap(res);
  },
};

export const NewJobMutations = {
  async deleteNewJobRecord(record: IdProvider) {
    if (!record?.id) {
      return;
    }
    const res = await axios.delete<void>(`/record/new_job/${record.id}/`);
    return unwrap(res);
  },
  bulkDeleteNewJobRecord(filterState: {
    employee: number | undefined;
    project: number | undefined;
  }) {
    return axios
      .post<void>('/record/new_job/bulk/delete/', {
        filterState,
      })
      .then(unwrap);
  },
  async createNewJobFromRecord(
    request: IdProvider & { department: number; project: number },
  ) {
    if (!request?.id) {
      return null;
    }
    const res = await axios.post<Job>(
      `/record/new_job/${request.id}/create_job/`,
      { department: request.department, project: request.project },
    );
    return unwrap(res);
  },
};

export const NewJobHooks = {
  useGetNewJobQuery(
    newJob: IdProvider,
    options?: UseQueryOptions<NewJob | null>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [NewJobKeys.NEWJOB, newJob?.id],
      queryFn: () => NewJobQueries.getNewJob(newJob),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([NewJobKeys.NEWJOB, newJob?.id]),
    ];
    return QueryResultAndInvalidate;
  },
  useNewJobRecordCount(options?: UseQueryOptions<{ count: number }>) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [NewJobKeys.NEWJOBRECORDCOUNT],
      queryFn: NewJobQueries.getNewJobRecordCount,
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORDCOUNT]),
    ];
    return QueryResultAndInvalidate;
  },
  useNewJobRecords(
    filterState: { employee?: number; project?: number },
    options?: UseQueryOptions<NewJobRecord[]>,
  ) {
    const queryClient = useQueryClient();
    const result = useQuery({
      queryKey: [NewJobKeys.NEWJOBRECORD, filterState],
      queryFn: () => NewJobQueries.getAllNewJobRecords(filterState),
      ...(options ?? {}),
    });
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORD]),
    ];
    return QueryResultAndInvalidate;
  },
  useCreateJobFromRecord() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: NewJobMutations.createNewJobFromRecord,
      onMutate(data) {
        const queryData: NewJobRecord[] | undefined = queryClient.getQueryData([
          NewJobKeys.NEWJOBRECORD,
        ]);
        queryClient.setQueryData(
          [NewJobKeys.NEWJOBRECORD],
          queryData?.filter((record) => record.id !== data.id),
        );
        return queryData;
      },
      onError(_err, _vars, context) {
        queryClient.setQueryData([NewJobKeys.NEWJOBRECORD], context);
      },
      async onSuccess() {
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORD]);
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORDCOUNT]);
      },
    });
  },
  useBulkDeleteJobRecord() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (filterState: {
        employee: number | undefined;
        project: number | undefined;
      }) => NewJobMutations.bulkDeleteNewJobRecord(filterState),

      async onSuccess() {
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORD]);
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORDCOUNT]);
      },
    });
  },
  useDeleteJobRecord() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: NewJobMutations.deleteNewJobRecord,
      onMutate(data) {
        const queryData: NewJobRecord[] | undefined = queryClient.getQueryData([
          NewJobKeys.NEWJOBRECORD,
        ]);
        queryClient.setQueryData(
          [NewJobKeys.NEWJOBRECORD],
          queryData?.filter((record) => record.id !== data?.id),
        );
        return queryData;
      },
      onError(_err, _vars, context) {
        queryClient.setQueryData([NewJobKeys.NEWJOBRECORD], context);
      },
      async onSuccess() {
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORD]);
        await queryClient.invalidateQueries([NewJobKeys.NEWJOBRECORDCOUNT]);
      },
    });
  },
};

export const NewJobApi = {
  queries: NewJobQueries,
  mutations: NewJobMutations,
  hooks: NewJobHooks,
};
